import {
  analysis_reference_area_enum,
  cbi_extraction_state_enum,
  cbi_invoice_type_enum,
  class_of_use_enum,
  consumption_type_enum,
  emission_factor_source_enum,
  type_of_use_enum,
} from '@predium/enums';
import { parseAsArrayOf, parseAsBoolean, parseAsInteger, parseAsString, parseAsStringEnum } from 'nuqs';
import { BUILDING_FILTERS_SEARCH_PARAMS } from '@/components/building-filters/search-params';
import {
  BuildingSubTabEnum,
  BuildingTabEnum,
  ClimateRiskSubTabEnum,
  ConsumptionHubTabEnum,
  ConsumptionInvoicesTabEnum,
  DataCollectionBuildingsTabEnum,
  DataCollectionEnergySuppliersTabEnum,
  DataCollectionTabActionsEnum,
  DisplayBaseEnum,
  PortfolioDisplayViewEnum,
  ScenariosTabEnum,
  StatusQuoAnalysisTabEnum,
} from '@/routes/search_params/searchParamsEnums';
import { RiskScenarioEnum } from '@/sections/DataCollection/Building/ClimateRiskV2/mapping';
import { ViewModeEnum } from '@/sections/DataCollection/Building/ClimateRiskV2/types';
import { ConsumptionTabNamesEnum } from '@/sections/DataCollection/Building/Consumption/AreaConsumptionListTabs';
import { ImpactTrendGraphEnum } from '@/sections/Scenarios/Scenario/types';

/**
 * The consumption hub's sub-tab. Its own key (not `tab`) because the hub also renders inside the
 * building detail page, whose `tab` param already selects the consumption tab itself.
 */
const CONSUMPTION_HUB_SEARCH_PARAMS = {
  consumptionTab: parseAsStringEnum<ConsumptionHubTabEnum>(Object.values(ConsumptionHubTabEnum)).withDefault(
    ConsumptionHubTabEnum.GAP_ANALYSIS,
  ),
};

export const SEARCH_PARAMS = {
  statusQuoAnalysis: {
    buildings: {
      tab: parseAsStringEnum<StatusQuoAnalysisTabEnum>(Object.values(StatusQuoAnalysisTabEnum)).withDefault(
        StatusQuoAnalysisTabEnum.DEMAND,
      ),
      benchmark: parseAsInteger, // null = no comparison; 0 = entire company; positive = a portfolio
      displayBase: parseAsStringEnum<DisplayBaseEnum>(Object.values(DisplayBaseEnum))
        .withDefault(DisplayBaseEnum.PER_SQUARE_METER)
        .withOptions({ clearOnDefault: true }),
      referenceAreaType: parseAsStringEnum<analysis_reference_area_enum>(Object.values(analysis_reference_area_enum)),
      emissionFactorSource: parseAsStringEnum<emission_factor_source_enum>(Object.values(emission_factor_source_enum))
        .withDefault(emission_factor_source_enum.NATIONAL_ENERGY_LAW)
        .withOptions({ clearOnDefault: true }),
    },
    building: {
      tab: parseAsStringEnum<StatusQuoAnalysisTabEnum>(Object.values(StatusQuoAnalysisTabEnum)).withDefault(
        StatusQuoAnalysisTabEnum.DEMAND,
      ),
      benchmark: parseAsInteger, // null = no comparison; 0 = entire company; positive = a portfolio
      classOfUse: parseAsStringEnum<class_of_use_enum>(Object.values(class_of_use_enum)),
      typeOfUse: parseAsStringEnum<type_of_use_enum>(Object.values(type_of_use_enum)),
      displayBase: parseAsStringEnum<DisplayBaseEnum>(Object.values(DisplayBaseEnum))
        .withDefault(DisplayBaseEnum.PER_SQUARE_METER)
        .withOptions({ clearOnDefault: true }),
      referenceAreaType: parseAsStringEnum<analysis_reference_area_enum>(Object.values(analysis_reference_area_enum)),
      emissionFactorSource: parseAsStringEnum<emission_factor_source_enum>(Object.values(emission_factor_source_enum))
        .withDefault(emission_factor_source_enum.NATIONAL_ENERGY_LAW)
        .withOptions({ clearOnDefault: true }),
    },
    economicUnits: {
      tab: parseAsStringEnum<StatusQuoAnalysisTabEnum>(Object.values(StatusQuoAnalysisTabEnum)).withDefault(
        StatusQuoAnalysisTabEnum.DEMAND,
      ),
      benchmark: parseAsInteger, // null = no comparison; 0 = entire company; positive = a portfolio
      currentView: parseAsStringEnum<PortfolioDisplayViewEnum>(Object.values(PortfolioDisplayViewEnum))
        .withDefault(PortfolioDisplayViewEnum.DASHBOARD)
        .withOptions({ clearOnDefault: true }),
      displayBase: parseAsStringEnum<DisplayBaseEnum>(Object.values(DisplayBaseEnum))
        .withDefault(DisplayBaseEnum.PER_SQUARE_METER)
        .withOptions({ clearOnDefault: true }),
      emissionFactorSource: parseAsStringEnum<emission_factor_source_enum>(Object.values(emission_factor_source_enum))
        .withDefault(emission_factor_source_enum.NATIONAL_ENERGY_LAW)
        .withOptions({ clearOnDefault: true }),
      includeNewConstruction: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
      referenceAreaType: parseAsStringEnum<analysis_reference_area_enum>(Object.values(analysis_reference_area_enum)),
      ...BUILDING_FILTERS_SEARCH_PARAMS,
    },
    portfolio: {
      tab: parseAsStringEnum<StatusQuoAnalysisTabEnum>(Object.values(StatusQuoAnalysisTabEnum)).withDefault(
        StatusQuoAnalysisTabEnum.DEMAND,
      ),
      benchmark: parseAsInteger, // null = no comparison; 0 = entire company; positive = a portfolio
      currentView: parseAsStringEnum<PortfolioDisplayViewEnum>(Object.values(PortfolioDisplayViewEnum))
        .withDefault(PortfolioDisplayViewEnum.DASHBOARD)
        .withOptions({ clearOnDefault: true }),
      displayBase: parseAsStringEnum<DisplayBaseEnum>(Object.values(DisplayBaseEnum))
        .withDefault(DisplayBaseEnum.PER_SQUARE_METER)
        .withOptions({ clearOnDefault: true }),
      emissionFactorSource: parseAsStringEnum<emission_factor_source_enum>(Object.values(emission_factor_source_enum))
        .withDefault(emission_factor_source_enum.NATIONAL_ENERGY_LAW)
        .withOptions({ clearOnDefault: true }),
      includeNewConstruction: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
      referenceAreaType: parseAsStringEnum<analysis_reference_area_enum>(Object.values(analysis_reference_area_enum)),
      ...BUILDING_FILTERS_SEARCH_PARAMS,
    },
  },
  actionPlanning: {
    scenario: {
      bulkAction: parseAsBoolean.withDefault(false).withOptions({
        clearOnDefault: true,
      }),
      bulkActionConflict: parseAsBoolean,
      bulkBuildingSearch: parseAsString.withDefault('').withOptions({
        clearOnDefault: true,
      }),
      id: parseAsInteger,
      graph: parseAsStringEnum<ImpactTrendGraphEnum>(Object.values(ImpactTrendGraphEnum))
        .withDefault(ImpactTrendGraphEnum.CO_2_EMISSIONS)
        .withOptions({
          clearOnDefault: true,
        }),
      showInvestment: parseAsBoolean.withDefault(false).withOptions({ clearOnDefault: true }),
      displayBase: parseAsStringEnum<DisplayBaseEnum>(Object.values(DisplayBaseEnum))
        .withDefault(DisplayBaseEnum.PER_SQUARE_METER)
        .withOptions({ clearOnDefault: true }),
      referenceAreaType: parseAsStringEnum<analysis_reference_area_enum>(Object.values(analysis_reference_area_enum)),
      emissionFactorSource: parseAsStringEnum<emission_factor_source_enum>(Object.values(emission_factor_source_enum))
        .withDefault(emission_factor_source_enum.NATIONAL_ENERGY_LAW)
        .withOptions({ clearOnDefault: true }),
      includeNewConstruction: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
      ...BUILDING_FILTERS_SEARCH_PARAMS,
    },
    scenarios: {
      search: parseAsString.withDefault('').withOptions({
        clearOnDefault: true,
      }),
      portfolioIds: parseAsArrayOf(parseAsInteger).withDefault([]).withOptions({
        clearOnDefault: true,
      }),
      tab: parseAsStringEnum<ScenariosTabEnum>(Object.values(ScenariosTabEnum))
        .withDefault(ScenariosTabEnum.ACTIVE)
        .withOptions({ clearOnDefault: true }),
    },
  },
  report: {
    init: parseAsBoolean.withDefault(false),
  },
  reports: {
    createFromTemplate: parseAsString,
    createForBuilding: parseAsInteger,
    createForPortfolio: parseAsInteger,
  },
  dataCollection: {
    buildings: {
      tab: parseAsStringEnum<DataCollectionBuildingsTabEnum>(Object.values(DataCollectionBuildingsTabEnum)).withDefault(
        DataCollectionBuildingsTabEnum.BUILDINGS,
      ),
      value: parseAsStringEnum<DataCollectionBuildingsTabEnum>(Object.values(DataCollectionBuildingsTabEnum)),
      action: parseAsStringEnum<DataCollectionTabActionsEnum>(Object.values(DataCollectionTabActionsEnum)),
      ...BUILDING_FILTERS_SEARCH_PARAMS,
    },
    economicUnits: {
      ...BUILDING_FILTERS_SEARCH_PARAMS,
    },
    building: {
      tab: parseAsStringEnum<BuildingTabEnum>(Object.values(BuildingTabEnum)).withDefault(BuildingTabEnum.overview),
      consumptionsTab: parseAsStringEnum<ConsumptionTabNamesEnum>(Object.values(ConsumptionTabNamesEnum)).withDefault(
        ConsumptionTabNamesEnum.consumptions,
      ),
      riskScenario: parseAsArrayOf(parseAsStringEnum<RiskScenarioEnum>(Object.values(RiskScenarioEnum))).withDefault([
        RiskScenarioEnum.RCP_2_6,
        RiskScenarioEnum.RCP_4_5,
        RiskScenarioEnum.RCP_8_5,
      ]),
      riskViewMode: parseAsStringEnum<ViewModeEnum>(Object.values(ViewModeEnum)).withDefault(
        ViewModeEnum.RISK_CATEGORY,
      ),
      openEditDialog: parseAsBoolean.withDefault(false),
      subTab: parseAsStringEnum<BuildingSubTabEnum>(Object.values(BuildingSubTabEnum)).withDefault(
        BuildingSubTabEnum.technology,
      ),
      climateRiskSubTab: parseAsStringEnum<ClimateRiskSubTabEnum>(Object.values(ClimateRiskSubTabEnum)).withDefault(
        ClimateRiskSubTabEnum.preCheck,
      ),
      draftBuildingTwin: parseAsBoolean.withDefault(false),
      draftBuildingTwinOnboarding: parseAsBoolean.withDefault(false),
      modelHistory: parseAsBoolean.withDefault(false),
      previewModelId: parseAsInteger,
      ...CONSUMPTION_HUB_SEARCH_PARAMS,
    },
    energySuppliers: {
      tab: parseAsStringEnum<DataCollectionEnergySuppliersTabEnum>(Object.values(DataCollectionEnergySuppliersTabEnum))
        .withDefault(DataCollectionEnergySuppliersTabEnum.ALL)
        .withOptions({
          history: 'push',
        }),
      search: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
    },
    consumptionHub: {
      ...CONSUMPTION_HUB_SEARCH_PARAMS,
      portfolioIds: parseAsArrayOf(parseAsInteger).withDefault([]).withOptions({ clearOnDefault: true }),
      year: parseAsInteger, // null = all years
    },
    consumptionInvoices: {
      tab: parseAsStringEnum<ConsumptionInvoicesTabEnum>(Object.values(ConsumptionInvoicesTabEnum)).withDefault(
        ConsumptionInvoicesTabEnum.PROCESSING,
      ),
      provider: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      consumptionTypes: parseAsArrayOf(parseAsStringEnum<consumption_type_enum>(Object.values(consumption_type_enum)))
        .withDefault([])
        .withOptions({ clearOnDefault: true }),
      buildingAddress: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      extractionStates: parseAsArrayOf(
        parseAsStringEnum<cbi_extraction_state_enum>(Object.values(cbi_extraction_state_enum)),
      )
        .withDefault([])
        .withOptions({ clearOnDefault: true }),
      invoiceAddress: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      invoiceNumber: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      emailSubject: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      invoiceTypes: parseAsArrayOf(parseAsStringEnum<cbi_invoice_type_enum>(Object.values(cbi_invoice_type_enum)))
        .withDefault([])
        .withOptions({ clearOnDefault: true }),
    },
    consumptionAddresses: {
      invoiceAddress: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      prediumAddress: parseAsString.withDefault('').withOptions({ clearOnDefault: true }),
      assignmentMethods: parseAsArrayOf(parseAsStringEnum<'AUTO' | 'MANUAL'>(['AUTO', 'MANUAL']))
        .withDefault([])
        .withOptions({ clearOnDefault: true }),
    },
  },
};
